import React, { useEffect, useMemo } from "react";
import { Box, styled, ThemeProvider, Typography } from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import OnTheWay from "./onTheWay";
import GetMyGiftLogin from "./Login";
import { SignInCard, SignInContainer } from "../subComps/loginPieces";
import PickYourGift from "./pickYourGift";
import MakeItOfficial from "./makeItOfficial";
import { createCustomTheme } from "../account/theme";
import { Context } from "../../common/context";
import poweredbycolor from "../images/Powered-By-Color-Logo.jpg";
import streamers from "../images/Streamers_Transparent.png";
import { getSid, scrollToTop } from "../../common/utils";
import { ClickableText } from "../account/components/Typography";
import { LiLink } from "../../common/commonComps";
import type { AppContext, ContextValue, GetMyGiftOptions, Size } from "../../types";

const BottomImage = styled(`img`)({
    width: `100px`,
});

const BottomImageBox = styled(Box)({
    marginTop: `30px`,
    paddingBottom: 40,
    textAlign: `center`,
});

const CardTerms = styled(Box)(() => ({
    userSelect: `none`,
    color: `#777`,
    letterSpacing: `0.02em`,
    fontSize: 13,
    lineHeight: 1.5,
    padding: `0 15px`,
    fontWeight: `400`,
    margin: `0 auto`,
    maxWidth: 750,
    "& a": {
        color: `#777 !important`,
    },
    "& p": {
        margin: `0`,
    },
}));

interface GetMyGiftFlowProps {
    size: Size;
    context: AppContext;
    [key: string]: Size | AppContext | string | boolean | number | undefined;
}

const GetMyGiftFlow = (params: GetMyGiftFlowProps): React.ReactElement | null => {
    const location = useLocation();
    const navigate = useNavigate();
    const { size, context } = params;
    const [searchParams, setSearchParams] = useSearchParams();
    const getMyGiftOptions = useMemo(
        () =>
            context?.getMyGiftOptions && context.getMyGiftOptions !== `none`
                ? (JSON.parse(context.getMyGiftOptions as string) as GetMyGiftOptions)
                : context.getMyGiftOptions,
        [context?.getMyGiftOptions]
    ) as GetMyGiftOptions | string | undefined;

    const sl = searchParams.get(`sl`);
    const st = searchParams.get(`st`) ?? `0`;
    const mode = location.pathname.split(`/`)[2] ?? `gift`;

    useEffect(() => {
        if (location.pathname === `/getmygift` && !getSid()) {
            navigate(`/getmygift/login${location.search}`);
            scrollToTop();
        }
        if (sessionStorage.getItem(`sl`) && !location.pathname.includes(`login`)) {
            navigate(`/account/payouts`);
            scrollToTop();
        }
    }, [location]);

    const setStep = (v: string | number): void => {
        setSearchParams(prev => {
            prev.set(`st`, String(v));
            return prev;
        });
        window.scrollTo(0, 0);
    };

    const pickCard = (v: number): void => {
        setSearchParams(prev => {
            prev.set(`st`, `1`);
            prev.set(`sl`, String(v));
            return prev;
        });
        window.scrollTo(0, 0);
    };

    return (
        <Context.Consumer>
            {(value: ContextValue) =>
                !value.context?.adminConfig ? null : (
                    <ThemeProvider theme={createCustomTheme({ context: value.context })}>
                        <SignInContainer size={size} direction="column" justifyContent="space-between">
                            <Box
                                sx={{
                                    margin: `0 auto 20px`,
                                }}
                            >
                                <Box
                                    sx={{ maxHeight: `50px`, maxWidth: `100px` }}
                                    component="img"
                                    src={value.context?.adminConfig?.mall?.get_my_gift_logo}
                                />
                            </Box>

                            {mode === `gift` && getSid() && (
                                <SignInCard
                                    sx={{
                                        background: `url(${streamers}) top center / contain no-repeat;`,
                                        paddingTop: `32px`,
                                    }}
                                    size={size}
                                    wide
                                    variant="outlined"
                                >
                                    {st === `0` && <PickYourGift {...params} {...{ getMyGiftOptions, pickCard }} />}
                                    {st === `1` && (
                                        <MakeItOfficial
                                            {...params}
                                            {...{
                                                card: (getMyGiftOptions as GetMyGiftOptions)?.rewards?.[Number(sl)],
                                                setStep,
                                                getMyGiftOptions,
                                            }}
                                        />
                                    )}
                                    {st === `2` && (
                                        <OnTheWay
                                            {...{
                                                getMyGiftOptions: getMyGiftOptions as GetMyGiftOptions,
                                                sl: Number(sl),
                                            }}
                                            {...params}
                                        />
                                    )}
                                    {st === `0` && mode === `gift` && (
                                        <Typography
                                            sx={{
                                                textAlign: `left`,
                                                fontSize: `13px`,
                                                margin: `0 auto`,
                                                maxWidth: 750,
                                                padding: `0 5px`,
                                            }}
                                        >
                                            To deliver your rewards, we&apos;ll set up a Cashback Rewards account for
                                            you where you can securely access your rewards at any time. By continuing,
                                            you agree to our&nbsp;
                                            <span>
                                                <LiLink
                                                    to={context?.adminConfig?.mall?.Email_Settings?.Terms_URL ?? ``}
                                                    target="_blank"
                                                    variant="body2"
                                                    sx={{ alignSelf: `center` }}
                                                >
                                                    Terms
                                                </LiLink>
                                                &nbsp;
                                            </span>
                                            and&nbsp;
                                            <span>
                                                <LiLink
                                                    to={context?.adminConfig?.mall?.Email_Settings?.Privacy_URL ?? ``}
                                                    target="_blank"
                                                    variant="body2"
                                                    sx={{ alignSelf: `center` }}
                                                >
                                                    Privacy Policy
                                                </LiLink>
                                            </span>
                                        </Typography>
                                    )}
                                </SignInCard>
                            )}
                            {mode === `login` && <GetMyGiftLogin {...params} />}
                            <Box
                                sx={{
                                    justifyContent: `left`,
                                    maxWidth: 750,
                                    width: `100%`,
                                    margin: `0 auto`,
                                    padding: `15px 8px 0`,
                                }}
                            >
                                <ClickableText sx={{ fontSize: 13 }}>
                                    <LiLink
                                        target="_blank"
                                        to={value.context?.adminConfig?.mall?.content_page_links?.contact_us_url ?? ``}
                                    >
                                        {` `}
                                        Need Help?
                                    </LiLink>
                                </ClickableText>
                            </Box>
                        </SignInContainer>
                        {st === `0` && mode === `gift` && (
                            <CardTerms>
                                The merchants represented are not sponsors of the rewards or otherwise affiliated with
                                this company. The logos and other identifying marks attached are trademarks of and owned
                                by each represented company and/or its affiliates. Please visit each company&apos;s
                                website for additional terms and conditions.
                            </CardTerms>
                        )}

                        {st === `1` && mode === `gift` && (
                            <CardTerms
                                dangerouslySetInnerHTML={{
                                    __html:
                                        (getMyGiftOptions as GetMyGiftOptions)?.rewards?.[
                                            Number(sl)
                                        ]?.brandDisclaimer?.replace(/href="http/g, `target="_blank" href="http`) ?? ``,
                                }}
                            />
                        )}
                        {sessionStorage.getItem(`sp`) ? (
                            <BottomImageBox>
                                <BottomImage src={poweredbycolor} />
                            </BottomImageBox>
                        ) : null}
                    </ThemeProvider>
                )
            }
        </Context.Consumer>
    );
};

export default GetMyGiftFlow;
